export type ApprovalStatus = 'approved' | 'denied' | 'review' | 'pending' | null;

export interface ExpirationEntry {
  name: string;
  date: string;
}

export interface ProfileDocument {
  id: string;
  title: string;
  description?: string;
  downloadUrl?: string;
  approvalStatus?: ApprovalStatus;
  createdAt?: string;
  fieldValues?: Record<string, unknown>;
}

/**
 * Extracts expiration entries from field_values.
 * Handles multiple formats from the Directories LLM parser:
 * - { coverages: [{name, expiration_date}] } — array of coverage objects
 * - { "Coverage Name": "2026-12-10" } — flat key/value pairs with date-like values
 */
/**
 * Extracts expiration entries from field_values.
 *
 * The backend stores expirations as flat key-value pairs in inputFieldsData:
 *   { "expiration_date": "2026-12-10", "Commercial General Liability": "2026-12-10", ... }
 * The reserved key "expiration_date" is the primary expiration (skipped here).
 * All other date-valued keys are coverage-specific expirations.
 *
 * See: app/components/visitors/document-review-card.gts initExpirationEntries()
 */
export function extractExpirations(fieldValues?: Record<string, unknown>): ExpirationEntry[] {
  if (!fieldValues) return [];

  const datePattern = /^\d{4}-\d{2}-\d{2}/;
  const skipKeys = new Set(['issue_date', 'expiration_date', 'created_at', 'updated_at']);
  const entries: ExpirationEntry[] = [];

  for (const [key, value] of Object.entries(fieldValues)) {
    if (skipKeys.has(key)) continue;
    if (typeof value === 'string' && datePattern.test(value)) {
      entries.push({ name: key.replace(/_/g, ' '), date: formatExpirationDate(value) });
    }
  }
  return entries;
}

function parseLocalDate(isoDate: string): Date {
  // Date-only strings (2026-12-10) are parsed as UTC midnight by spec.
  // Convert to local midnight to prevent off-by-one display for non-UTC users.
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const parts = isoDate.split('-').map(Number);
    return new Date(parts[0] as number, (parts[1] as number) - 1, parts[2] as number);
  }
  return new Date(isoDate);
}

function formatExpirationDate(isoDate: string): string {
  try {
    const date = parseLocalDate(isoDate);
    if (isNaN(date.getTime())) return isoDate;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

export function formatUploadDate(isoDate?: string): string | undefined {
  if (!isoDate) return undefined;
  try {
    const date = parseLocalDate(isoDate);
    if (isNaN(date.getTime())) return undefined;
    return `Uploaded ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  } catch {
    return undefined;
  }
}

export interface StatusDisplay {
  label: string;
  bgClass: string;
  textClass: string;
}

const STATUS_CONFIG: Record<string, StatusDisplay> = {
  pending: { label: 'PENDING', bgClass: 'bg-papaya-10', textClass: 'text-[#A84F00]' },
  review: { label: 'PENDING', bgClass: 'bg-papaya-10', textClass: 'text-[#A84F00]' },
  approved: { label: 'APPROVED', bgClass: 'bg-cilantro-10', textClass: 'text-cilantro-60' },
  denied: { label: 'DENIED', bgClass: 'bg-red-10', textClass: 'text-red-70' },
};

export function formatDetailDate(isoDate?: string): string | undefined {
  if (!isoDate) return undefined;
  try {
    const date = parseLocalDate(isoDate);
    if (isNaN(date.getTime())) return undefined;
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    return `${datePart} \u2022 ${timePart}`;
  } catch {
    return undefined;
  }
}

export function getStatusDisplay(status: ApprovalStatus | undefined): StatusDisplay | null {
  if (!status) return null;
  return STATUS_CONFIG[status] ?? null;
}
